import { useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { Labels } from "../../../../../features/data-types"
import { RoutePath, useRoutesHooks } from "../../../../../features/routes"
import { Action, Async, Badge, DetailFieldProps, DetailPanel, FieldType, FieldValue, Link, ModalId, PDFTabContent, Tab, UIPermission, useGetCrumbs, useUIActions } from "../../../../../features/ui"
import { addressHelpers } from "../../../../../features/address"
import { TableName } from "../../../../../tables"
import { BranchBadge } from "../../../../manager"
import { Operation, OperationLink, operationHelpers } from "../../../../operations"
import { Customer, CustomerLink } from "../../../../sales"
import { useSystemStateHooks } from "../../../../system"
import { deliveryHelpers } from "../../../hooks/deliveryHooks"
import { deliveryOrderHelpers, useDeliveryOrderHooks } from "../../../hooks/deliveryOrderHooks"
import { Delivery, DeliveryMethod, DeliveryOrder, DeliveryStatus } from "../../../state/types"
import { AnnulDeliveryModal } from "../../delivery/annul-delivery-modal/annulDeliveryModal"
import { DeliveryItemsStatsTable } from "../../delivery/delivery-items-stats-table/deliveryItemsStatsTable"
import { DeliveryNotePDF } from "../../delivery/delivery-note-pdf/deliveryNotePDF"
import { DownloadDeliveryNoteModal } from "../../delivery/download-delivery-note-modal/downloadDeliveryNoteModal"
import { UploadDeliveryNoteModal } from "../../delivery/upload-delivery-note-modal/uploadDeliveryNoteModal"

type Props = {
    delivery: Delivery
    deliveryOrder?: DeliveryOrder
    operation: Operation
    customer?: Customer
    onRefresh: () => Promise<void>
}

export const DeliveryNoteDetail = (props: Props) => {
    const { delivery, deliveryOrder, operation, customer, onRefresh } = props
    const { items, fromNotDeliveredItems, ...data } = delivery

    const { describeDeliveryNote, isCompletable, isAnnulable, isReturnable } = deliveryHelpers
    const { describeDeliveryOrder, getDeliveryMethodIcon, getDeliveryMethodRoutPath } = deliveryOrderHelpers
    const { describeAddress } = addressHelpers
    const { getDeliveryAddress } = useDeliveryOrderHooks()
    const { userHasPermission } = useSystemStateHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_SALE_DELIVERIES)
    const { getRoutePath } = useRoutesHooks()
    const navigate = useNavigate()

    const { openModal } = useUIActions()

    const isReturn = delivery.method === DeliveryMethod.RETURN
    const isReception = delivery.method === DeliveryMethod.RECEPTION

    const title = useMemo(() => describeDeliveryNote(delivery, deliveryOrder), [delivery, deliveryOrder])
    const crumbs = useGetCrumbs('Remitos', RoutePath.DELIVERY_NOTES, title)

    const fields: DetailFieldProps[] = [
        {
            name: 'method',
            type: FieldType.TEXT,
            label: 'Origen',
            render: (value?: FieldValue) => <>{Labels.deliveryMethodLabel(value! as DeliveryMethod)}</>
        },
        {
            name: 'operationId',
            type: FieldType.TABLE,
            table: delivery.table,
            label: 'Operación',
            render: () => <OperationLink table={delivery.table} operation={operation} />
        },
        {
            name: 'branchId',
            type: FieldType.TEXT,
            label: 'Sucursal',
            render: () => <BranchBadge branchId={delivery.branchId} />
        },
        {
            name: 'addressId',
            type: FieldType.TEXT,
            label: isReturn ? 'Dirección de Recepción' : 'Dirección de Entrega',
            render: () => <Async
                loadFn={async () => {
                    const address = await getDeliveryAddress(delivery)
                    return <>{describeAddress(address)}</>
                }}
            />
        },
        {
            name: 'date',
            type: FieldType.DATE,
            label: 'Fecha'
        },
        {
            name: 'items',
            type: FieldType.TABLE,
            render: () => <DeliveryItemsStatsTable delivery={delivery} operation={operation} />
        },
        {
            name: 'deliveryStatus',
            type: FieldType.TEXT,
            label: 'Estado de Entrega',
            render: (value?: FieldValue) => delivery.cancelled ?
                <Badge label="Anulado" /> :
                <>{Labels.deliveryStatusLabel(value! as DeliveryStatus)}</>
        }
    ]
    if (deliveryOrder) {
        fields.splice(2, 0, {
            name: 'orderId',
            type: FieldType.TABLE,
            table: TableName.DELIVERY_ORDERS,
            label: 'Orden',
            render: () => (
                <Link
                    text={describeDeliveryOrder(deliveryOrder)}
                    icon={getDeliveryMethodIcon(deliveryOrder.method)}
                    kind="primary"
                    onClick={() => navigate(getRoutePath(getDeliveryMethodRoutPath(deliveryOrder.method), deliveryOrder.id))}
                />
            )
        })
    }
    if (isReception) {
        fields.splice(1, 0, {
            name: 'supplierDeliveryNoteNumber',
            type: FieldType.TEXT,
            label: 'Nro. Remito del Proveedor',
            render: () => <>{delivery.supplierDeliveryNoteNumber || '-'}</>
        })
    }
    if (customer) {
        fields.splice(2, 0, {
            name: 'customerId',
            type: FieldType.TABLE,
            table: TableName.CUSTOMERS,
            label: 'Cliente',
            render: () => <CustomerLink customer={customer} />
        })
    }

    const tabs: Tab[] = useMemo(() => {
        const tabList: Tab[] = []
        if (!isReception) {
            tabList.push({
                id: 'document',
                title: 'Documento',
                content: (
                    <PDFTabContent>
                        <DeliveryNotePDF
                            id="delivery-note-preview"
                            delivery={delivery}
                            deliveryOrder={deliveryOrder}
                            previewMode
                        />
                    </PDFTabContent>
                )
            })
        }
        return tabList
    }, [delivery, deliveryOrder, isReception])

    const onDownload = () => openModal(ModalId.DOWNLOAD_DELIVERY_NOTE)

    const onUploadSigned = () => openModal(ModalId.UPLOAD_DELIVERY_NOTE)

    const onComplete = () => navigate(
        `${getRoutePath(RoutePath.SHIPMENT_ORDERS)}/${delivery.orderId}/${RoutePath.COMPLETE_DELIVERY}`,
        { state: { deliveryId: delivery.id } }
    )

    const onAnnul = () => openModal(ModalId.ANNUL_DELIVERY)

    const onRegisterReturn = () => navigate(
        `${getRoutePath(operationHelpers.getRoutePath(delivery.table), delivery.operationId)}/${RoutePath.REGISTER_RETURN}`
    )

    const actions: Action[] = useMemo(() => {
        const actionList: Action[] = []
        if (!isReception || delivery.deliveryNoteUrl) {
            actionList.push({ label: 'Descargar', icon: 'download', kind: 'tertiary', onClick: onDownload })
        }
        if (!readOnly) {
            if (!isReturn && !delivery.cancelled && !deliveryOrder?.cancelled && !delivery.deliveryNoteUrl) {
                actionList.push({ label: isReception ? 'Subir Remito del Proveedor' : 'Subir Remito Firmado', icon: 'upload', kind: 'tertiary', onClick: onUploadSigned })
            }
            if (isCompletable(delivery, deliveryOrder)) {
                actionList.push({ label: 'Completar Envío', icon: 'bag-check-fill', onClick: onComplete })
            }
            if (isReturnable(delivery, deliveryOrder)) {
                actionList.push({ label: 'Registrar Devolución', icon: 'arrow-return-left', onClick: onRegisterReturn })
            }
            if (isAnnulable(delivery, deliveryOrder)) {
                actionList.push({ label: 'Anular Entrega', icon: 'slash-circle', kind: 'secondary', onClick: onAnnul })
            }
        }
        return actionList
    }, [delivery, deliveryOrder, readOnly])

    return (
        <>
            <DetailPanel
                tableName={TableName.DELIVERIES2}
                title={title}
                crumbs={crumbs}
                fields={fields}
                data={data}
                icon={isReturn ? 'arrow-return-left' : 'file-spreadsheet'}
                imgSpace={3}
                readOnly
                actions={actions}
                tabs={tabs}
            />
            <DownloadDeliveryNoteModal
                delivery={delivery}
                deliveryOrder={deliveryOrder}
            />
            <UploadDeliveryNoteModal
                delivery={delivery}
                deliveryOrder={deliveryOrder}
                onUpload={onRefresh}
            />
            <AnnulDeliveryModal
                delivery={delivery}
                deliveryOrder={deliveryOrder}
                operation={operation}
                onAnnul={onRefresh}
            />
        </>
    )
}
